import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DerivativeChip from "./DerivativeChip";
import { RELATION_ORDER, type RelationType } from "@/lib/philology/relationTypes";
import type { DerivativeRecord } from "@/lib/philology/api";

interface WordFamilyTreeProps {
  derivatives: DerivativeRecord[];
  activeWord?: string | null;
  onSelect?: (d: DerivativeRecord) => void;
}

export default function WordFamilyTree({ derivatives, activeWord, onSelect }: WordFamilyTreeProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByRelation(derivatives), [derivatives]);

  if (derivatives.length === 0) {
    return (
      <div className="text-center py-12 text-[hsl(var(--muted-foreground))]">
        <p className="text-sm">{t("philology.empty")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {RELATION_ORDER.map((rel) => {
        const list = groups[rel];
        if (!list || list.length === 0) return null;
        return (
          <section key={rel} className="rounded-2xl border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] p-4 shadow-sm">
            <header className="mb-3 flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-[hsl(var(--foreground))]">
                {t(`philology.relationTypes.${rel}`)}
              </h3>
              <span className="text-xs text-[hsl(var(--muted-foreground))]">{list.length}</span>
            </header>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {list.map((d, i) => (
                <DerivativeChip
                  key={`${rel}-${d.word}-${i}`}
                  derivative={d}
                  isActive={activeWord != null && d.word === activeWord}
                  onClick={onSelect}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function groupByRelation(list: DerivativeRecord[]): Partial<Record<RelationType, DerivativeRecord[]>> {
  const out: Partial<Record<RelationType, DerivativeRecord[]>> = {};
  for (const d of list) {
    const key = d.relationType;
    if (!out[key]) out[key] = [];
    out[key]!.push(d);
  }
  return out;
}
